import * as React from 'react';
import { cva, VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { HTMLAttributeAnchorTarget } from 'react';
import { Spinner } from 'baseui/spinner';

export type ButtonProps = {
  id?: string;
  type?: 'submit' | 'reset' | 'button';
  disabled?: boolean;
  className?: string;
  component?: React.ElementType;
  loading?: boolean;
  href?: string;
  target?: HTMLAttributeAnchorTarget | undefined;
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
} & VariantProps<typeof buttonStyles>;

const buttonStyles = cva(
  twMerge(
    'inline-flex flex-row justify-center items-center',
    'font-haben text-white font-normal',
    'border-transparent border border-solid focus:border-[#FFFFFF]',
    'box-border',
    'rounded-full'
  ),
  {
    variants: {
      size: {
        unspecified: '',
        medium:
          'px-3 lg:px-9 py-1.5 lg:py-4 leading-[1.25rem] text-sm lg:text-xl',
        small: 'text-xs px-2 py-1',
      },
      style: {
        unspecified: '',
        link: 'text-[#FFFFFF] hover:text-[#FFFFFF]/90 focus:border-transparent',
        neutral:
          'bg-[#FFFFFF] hover:bg-[#FFFFFF]/90 border-[#D5D5D5] text-[#1E1614]',
        accent: 'bg-[#000000] border-[#FFFFFF] hover:bg-[#333333]',
        outline: 'border-[#F7F8F8]',
        ghost: 'text-[#B4BCD0]',
        'neutral-dark': 'bg-[#191A1E]',
        'outline-dark': 'bg-[#191A1E] border-[#0033FF]',
      },
      disabled: {
        true: 'cursor-not-allowed bg-[#565656] active:bg-[#565656] focus:bg-[#565656] focus:border-transparent',
        false: '',
      },
    },
    defaultVariants: {
      size: 'medium',
      style: 'neutral',
    },
  }
);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function (props, ref) {
    const {
      id,
      disabled = false,
      size = 'medium',
      style,
      children,
      className,
      loading,
      component: Component = 'button',
      href,
      target,
      type = 'button',
      onClick = () => {},
    } = props;

    const classNames = twMerge(
      buttonStyles({ size, style, disabled }),
      className
    );

    const handleClick = disabled ? () => {} : onClick;

    return (
      <Component
        ref={ref}
        id={id}
        href={href}
        target={target}
        type={type}
        className={classNames}
        onClick={handleClick}
      >
        {children}
        {loading && (
          <Spinner className='ml-1' $size='14px' $borderWidth='3px' />
        )}
      </Component>
    );
  }
);
